import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { hideChildModal, hideModal } from "../../data/modal";

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  LinearProgress,
  Typography,
} from "@mui/material";

import {
  Close as CloseIcon,
} from "@mui/icons-material";

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url
).toString();

const MINIMUM_PDF_LOADER_TIME = 450;

const getFileContentUrl = (file: any) => {
  return file?.contentUrl ?? file?.url ?? "";
};

const getFileName = (file: any) => {
  return (
    file?.description ??
    file?.discription ??
    file?.name ??
    file?.fileName ??
    file?.filename ??
    "File"
  );
};

const getFileExtension = (file: any) => {
  const mediaType = String(
    file?.mimeType ?? file?.mediaType ?? file?.contentType ?? file?.type ?? ""
  ).toLowerCase();

  if (mediaType.includes("pdf")) return "pdf";
  if (mediaType.includes("png")) return "png";
  if (mediaType.includes("jpeg") || mediaType.includes("jpg")) return "jpg";

  const sources = [
    file?.description,
    file?.discription,
    file?.originalName,
    file?.name,
    file?.fileName,
    file?.filename,
    file?.path,
    file?.contentUrl,
    file?.url,
  ];

  for (const source of sources) {
    if (!source) continue;

    const cleanSource = String(source).split("?")[0].split("#")[0];
    const match = cleanSource.match(/\.([a-z0-9]+)$/i);

    if (match) return match[1].toLowerCase();
  }

  return "";
};

const FileView = ({ file, includeButtons = true, isChildModal = false }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const modal = useSelector((state: any) => state.modal);

  const fileUrl = getFileContentUrl(file);
  const fileName = getFileName(file);
  const extension = getFileExtension(file);

  const pdfLoadStartedAtRef = useRef<number>(0);
  const pdfLoaderTimeoutRef = useRef<any>(null);

  const options = useMemo(
    () => ({
      cMapUrl: "/cmaps/",
      standardFontDataUrl: "/standard_fonts/",
    }),
    []
  );

  const [numPages, setNumPages] = useState<number>();
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [searchText, setSearchText] = useState("");
  const [pdfLoading, setPdfLoading] = useState(extension === "pdf");
  const [pageLoading, setPageLoading] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);

  useEffect(() => {
    if (pdfLoaderTimeoutRef.current) {
      clearTimeout(pdfLoaderTimeoutRef.current);
    }

    setNumPages(undefined);
    setPageNumber(1);
    setSearchText("");
    setPdfError(null);
    setPageLoading(false);
    setPdfLoading(extension === "pdf");
    pdfLoadStartedAtRef.current = Date.now();

    return () => {
      if (pdfLoaderTimeoutRef.current) {
        clearTimeout(pdfLoaderTimeoutRef.current);
      }
    };
  }, [fileUrl, extension]);

  const hidePdfLoaderWithMinimumDelay = useCallback(() => {
    const elapsed = Date.now() - pdfLoadStartedAtRef.current;
    const remainingDelay = Math.max(MINIMUM_PDF_LOADER_TIME - elapsed, 0);

    if (pdfLoaderTimeoutRef.current) {
      clearTimeout(pdfLoaderTimeoutRef.current);
    }

    pdfLoaderTimeoutRef.current = setTimeout(() => {
      setPdfLoading(false);
      setPageLoading(false);
    }, remainingDelay);
  }, []);

  const highlightPattern = (text: string, pattern: string) => {
    if (!pattern) {
      return text;
    }

    try {
      return text.replace(new RegExp(pattern, "gi"), (value) => {
        return `<mark>${value}</mark>`;
      });
    } catch {
      return text;
    }
  };

  const onChange = (event: any) => {
    setSearchText(event.target.value);
  };

  const onDocumentLoadStart = () => {
    setPdfError(null);
    setPdfLoading(true);
    pdfLoadStartedAtRef.current = Date.now();
  };

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }): void => {
    setNumPages(numPages);
    setPageNumber(1);
    hidePdfLoaderWithMinimumDelay();
  };

  const onDocumentLoadError = (error: any) => {
    console.error(error);

    setPdfLoading(false);
    setPageLoading(false);
    setPdfError(
      t(
        "pdfLoadFailed",
        "The PDF could not be loaded. Please try again or download the file."
      )
    );
  };

  const onPageLoadStart = () => {
    setPageLoading(true);
  };

  const onPageLoadSuccess = () => {
    setPageLoading(false);
  };

  const onPageLoadError = (error: any) => {
    console.error(error);

    setPageLoading(false);
    setPdfError(
      t("pdfPageRenderFailed", "The PDF page could not be rendered correctly.")
    );
  };

  const textRenderer = useCallback(
    (textItem: any) => highlightPattern(textItem.str, searchText),
    [searchText]
  );

  const changePage = (offset: number) => {
    setPdfError(null);
    setPageLoading(true);
    setPageNumber((prevPageNumber) => prevPageNumber + offset);
  };

  const previousPage = () => {
    changePage(-1);
  };

  const nextPage = () => {
    changePage(1);
  };

  const handleClose = () => {
    if (isChildModal || modal.childModal) {
      dispatch(hideChildModal() as any);
      return;
    }

    dispatch(hideModal() as any);
  };

  return (
    <Grid container>
      <Grid
        item
        xs={12}
        sx={{
          maxWidth: `${includeButtons ? "80vh" : "50vh"} !important`,
          overflow: "auto",
        }}
      >
        {extension === "pdf" ? (
          <>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 2,
                flexWrap: "wrap",
              }}
            >
              <Grid item>
                <label htmlFor="search">{t("search", "Search")}:&nbsp;</label>
                <input
                  type="search"
                  id="search"
                  value={searchText}
                  onChange={onChange}
                  disabled={pdfLoading || Boolean(pdfError)}
                />
              </Grid>

              <Grid item>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  {numPages && numPages > 1 ? (
                    <>
                      <Button
                        type="button"
                        disabled={pageNumber <= 1 || pdfLoading || pageLoading}
                        onClick={previousPage}
                      >
                        {t("previous", "Previous")}
                      </Button>

                      <Typography fontSize={14}>
                        {t("pageOf", {
                          page: pageNumber || (numPages ? 1 : "--"),
                          pages: numPages || "--",
                          defaultValue: "Page {{page}} of {{pages}}",
                        })}
                      </Typography>

                      <Button
                        type="button"
                        disabled={
                          pageNumber >= numPages || pdfLoading || pageLoading
                        }
                        onClick={nextPage}
                      >
                        {t("next", "Next")}
                      </Button>
                    </>
                  ) : null}
                </Box>
              </Grid>
            </Box>

            <hr />

            {pdfError ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                {pdfError}
              </Alert>
            ) : null}

            <Box
              sx={{
                position: "relative",
                maxHeight: "700px",
                minHeight: 260,
                overflow: "auto",
                borderRadius: 1,
                backgroundColor: "background.default",
              }}
            >
              {(pdfLoading || pageLoading) && (
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    zIndex: 10,
                    backgroundColor: "rgba(255, 255, 255, 0.78)",
                    backdropFilter: "blur(1px)",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 2,
                    p: 3,
                  }}
                >
                  <CircularProgress size={34} />

                  <Box sx={{ width: "60%", maxWidth: 320 }}>
                    <LinearProgress />
                  </Box>

                  <Typography variant="body2" color="text.secondary">
                    {t("loadingPdfPreview", "Loading PDF preview...")}
                  </Typography>
                </Box>
              )}

              <Document
                key={fileUrl}
                file={fileUrl}
                onLoadStart={onDocumentLoadStart}
                onLoadSuccess={onDocumentLoadSuccess}
                onLoadError={onDocumentLoadError}
                options={options}
                loading={null}
                error={null}
                noData={
                  <Alert severity="warning">
                    {t("noPdfFileForPreview", "No PDF file was found for this preview.")}
                  </Alert>
                }
              >
                <Page
                  key={`${fileUrl}-${pageNumber}`}
                  pageNumber={pageNumber}
                  customTextRenderer={textRenderer}
                  onLoadStart={onPageLoadStart}
                  onLoadSuccess={onPageLoadSuccess}
                  onLoadError={onPageLoadError}
                  loading={null}
                  error={null}
                  width={includeButtons ? 760 : 480}
                />
              </Document>
            </Box>
          </>
        ) : null}

        {["png", "jpeg", "jpg"].includes(extension ?? "") ? (
          <Box
            component="img"
            src={fileUrl}
            height={includeButtons ? 700 : 500}
            alt={fileName}
            sx={{
              maxWidth: "100%",
              objectFit: "contain",
              display: "block",
            }}
          />
        ) : null}

        {!["pdf", "png", "jpeg", "jpg"].includes(extension ?? "") ? (
          <Alert severity="info">
            {t("fileTypeCannotBePreviewed", "This file type cannot be previewed here.")}
          </Alert>
        ) : null}
      </Grid>

      {includeButtons ? (
        <>
          <Grid item xs={12}>
            <Divider />
          </Grid>

          <Grid
            item
            xs={12}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              paddingTop: "5px",
            }}
          >
            <Button onClick={handleClose} variant="contained">
              <CloseIcon fontSize="inherit" />
              &nbsp;{t("close", "Close")}
            </Button>
          </Grid>
        </>
      ) : null}
    </Grid>
  );
};

export default FileView;
